import React, { useState, useEffect } from 'react';

const DaycareUnfreezeModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  enrollment,
  apiCall,
  showError,
  showSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [addCharge, setAddCharge] = useState(false);
  const [chargeMonth, setChargeMonth] = useState('');
  const [chargeYear, setChargeYear] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [reason, setReason] = useState('');
  
  // Calculate amounts
  const baseAmount = enrollment?.FinalMonthlyRate || 0;
  const discountAmount = baseAmount * (parseFloat(discountPercent) || 0) / 100;
  const finalAmount = baseAmount - discountAmount;

  useEffect(() => {
    if (isOpen && enrollment) {
      // Set default month to current month
      const now = new Date();
      setChargeMonth(now.getMonth() + 1);
      setChargeYear(now.getFullYear());
      
      // Calculate suggested discount based on freeze period
      if (enrollment.FreezeStartDate && enrollment.FreezeEndDate) {
        const start = new Date(enrollment.FreezeStartDate);
        const end = new Date(enrollment.FreezeEndDate);
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const frozenDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        const suggestedDiscount = Math.min(100, Math.round((frozenDays / daysInMonth) * 100));
        setDiscountPercent(suggestedDiscount);
        setReason(`Partial month - frozen ${frozenDays} days`);
      }
    }
  }, [isOpen, enrollment]);

  const handleUnfreeze = async (e) => {
    e.preventDefault();
    
    if (addCharge) {
      if (!chargeMonth || !chargeYear) {
        showError('Please select a month and year for the charge');
        return;
      }
      if (!discountPercent || parseFloat(discountPercent) < 0 || parseFloat(discountPercent) > 100) {
        showError('Please enter a valid discount percentage (0-100)');
        return;
      }
      if (!reason.trim()) {
        showError('Please provide a reason for the discounted charge');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await apiCall(`/daycare/enrollments/${enrollment.EnrollmentID}/unfreeze`, {
        method: 'PUT',
        body: JSON.stringify({
          addCharge,
          chargeMonth: addCharge ? chargeMonth : null,
          chargeYear: addCharge ? chargeYear : null,
          discountPercent: addCharge ? parseFloat(discountPercent) : null,
          reason: addCharge ? reason : null
        })
      });

      if (addCharge && response.chargeCreated) {
        showSuccess(
          `Enrollment unfrozen for ${enrollment.FirstName} ${enrollment.LastName}. ` +
          `Charge created: $${response.chargeAmount} for ${response.chargeMonthName}`
        );
      } else {
        showSuccess(`Enrollment unfrozen for ${enrollment.FirstName} ${enrollment.LastName}`);
      }
      
      onSuccess();
      handleClose();
    } catch (error) {
      showError('Error unfreezing enrollment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAddCharge(false);
    setDiscountPercent('');
    setReason('');
    onClose();
  };

  if (!isOpen || !enrollment) return null;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Unfreeze Enrollment</h2>
            <p className="text-green-100 text-sm mt-1">
              Restore active status
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Enrollment Info */}
        <div className="px-6 py-4 bg-green-50 border-b border-green-200">
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Child:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {enrollment.FirstName} {enrollment.LastName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Package:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {enrollment.PackageName} (${baseAmount.toFixed(2)}/month)
              </span>
            </div>
            <div>
              <span className="text-gray-600">Current Status:</span>
              <span className="ml-2 font-semibold text-blue-600">
                🧊 Frozen
              </span>
            </div>
            {enrollment.FreezeStartDate && enrollment.FreezeEndDate && (
              <div>
                <span className="text-gray-600">Freeze Period:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {new Date(enrollment.FreezeStartDate).toLocaleDateString()} - {new Date(enrollment.FreezeEndDate).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleUnfreeze} className="p-6 space-y-4">
          
          {/* Add Charge Option */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="addCharge"
                checked={addCharge}
                onChange={(e) => setAddCharge(e.target.checked)}
                className="mt-1 mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <label htmlFor="addCharge" className="block text-sm font-medium text-gray-900 cursor-pointer">
                  Add discounted charge for partial month
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Check this if the student was frozen mid-month and you want to charge for the days attended.
                </p>
              </div>
            </div>
          </div>

          {/* Charge Details (shown when checkbox is checked) */}
          {addCharge && (
            <div className="space-y-4 pl-2 border-l-4 border-green-500">
              
              {/* Month Selection */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Month <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={chargeMonth}
                    onChange={(e) => setChargeMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required={addCharge}
                  >
                    <option value="">Select...</option>
                    {monthNames.map((month, idx) => (
                      <option key={idx} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={chargeYear}
                    onChange={(e) => setChargeYear(e.target.value)}
                    min="2020"
                    max="2030"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required={addCharge}
                  />
                </div>
              </div>

              {/* Discount Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount % <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                  min="0"
                  max="100"
                  step="1"
                  placeholder="e.g., 50 for 50% discount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required={addCharge}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter 0-100. Example: 50 = half price
                </p>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Partial month - returned mid-month"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required={addCharge}
                />
              </div>

              {/* Amount Preview */}
              {discountPercent && (
                <div className="bg-white border-2 border-green-300 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-700 mb-2">Charge Preview:</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Amount:</span>
                      <span className="font-medium">${baseAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Discount ({discountPercent}%):</span>
                      <span className="font-medium">-${discountAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-green-200 text-green-700 font-bold">
                      <span>Final Amount:</span>
                      <span>${finalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info Box */}
          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-green-800">
                <p className="font-medium">What happens when unfrozen:</p>
                <ul className="mt-1 space-y-1 list-disc list-inside text-xs">
                  <li>Status restored to "Active"</li>
                  <li>Regular billing resumes next month</li>
                  <li>Freeze dates kept for history</li>
                  {addCharge && <li className="font-semibold">Discounted charge will be created</li>}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Unfreezing...' : '✅ Unfreeze & Restore'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DaycareUnfreezeModal;
